<template>
	<section>
		<ButtonBack />
		<el-row>
			<h1>Fälle</h1>
		</el-row>

		<h2>Filter</h2>
		<FaelleSearchForm @doSearch="doSearch" :id="formId" ref="faelleSearchForm" />
	</section>

	<div class="bg--white">
		<section id="searchresults">
			<h2>Fälle ({{ $getCountErgebnisseLabel(casesTotalCount) }})</h2>
			<CasesTable :tableData="filteredCases" :loading="casesTableLoading" :totalCount="casesTotalCount" @loadNextPage="loadNextPage" />
		</section>
	</div>
</template>

<script lang="ts">
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import CasesTable from '@/components/tables/CasesTable.vue';
import TheButton from '@/components/TheButton.vue';
import TheForm from '@/components/TheForm.vue';
import { searchCases } from '@/requests/searches.js';
import { CaseResponse, CaseState, SearchCaseRequest } from '@/types/types';
import { returnFormOptions } from '@/utils/formOptions.js';
import { prepareSearchRequest } from '@/utils/search/searchUtils';
import { validateForm } from '@/utils/functions';
import { Close, Plus } from '@element-plus/icons-vue';
import { FormInstance } from 'element-plus';
import { defineComponent, ref } from 'vue';
import FaelleSearchForm from '@/components/forms/FaelleSearchForm.vue';

interface SearchCasesData {
	request: SearchCaseRequest;
	rules: Object;
	formId: string;
	tablePage: number;
	itemsPerPage: number;
	filteredCases: CaseResponse[];
	casesTotalCount: number;
	casesTableLoading: boolean;
}

export default defineComponent({
	name: 'FaelleView',
	data(): SearchCasesData {
		return {
			request: {
				searchTerm: '',
				category: null,
				medium: null,
				states: null,
				isPublished: null,
				fromDate: null,
				toDate: null,
				reach: null,
				sector: null,
			},
			rules: {},
			formId: 'searchForm',
			tablePage: 0,
			itemsPerPage: 14,
			filteredCases: [],
			casesTotalCount: 0,
			casesTableLoading: false,
		};
	},
	setup() {
		const formRef = ref<FormInstance>();
		return {
			formRef,
		};
	},
	components: {
		ButtonBack,
		TheButton,
		TheForm,
		Plus,
		Close,
		CasesTable,
		FaelleSearchForm,
	},
	computed: {
		categoryOptions() {
			return returnFormOptions('complaintAndCaseCategories', false, true);
		},
		mediaOptions() {
			return returnFormOptions('media', false, true);
		},
		sectorOptions() {
			return returnFormOptions('sector', false, true);
		},
		reachOptions() {
			return returnFormOptions('reach', false, true);
		},
		statusOptions() {
			return returnFormOptions('caseStateOptions', false, true);
		},
		isPublicOptions() {
			return returnFormOptions('isPublic', false, true);
		},
		timePeriodOptions() {
			return returnFormOptions('timePeriod');
		},
	},
	methods: {
		async doSearch() {
			const searchFormRef = this.$refs.faelleSearchForm as InstanceType<typeof FaelleSearchForm>;
			const formRef = searchFormRef.$refs.formRef as FormInstance;

			const isValid = await validateForm(formRef, this.formId);
			if (!isValid) return;

			this.casesTableLoading = true;
			let searchRequest = prepareSearchRequest(searchFormRef.request) as SearchCaseRequest;
			if (searchRequest.states) {
				// @ts-ignore
				searchRequest.states = [searchRequest.states];
			}
			// Fasse die Prüfungsstatus zusammen (wichtig für backend search)
			if (searchRequest.states?.includes(CaseState.ExaminationSmallSenate)) {
				searchRequest.states.push(CaseState.ExaminationSmallSenateRunning);
			}
			if (searchRequest.states?.includes(CaseState.ExaminationWerberat)) {
				searchRequest.states.push(CaseState.ExaminationWerberatRunning);
			}

			const searchResult = await searchCases(searchRequest, this.tablePage, this.itemsPerPage);
			if (searchResult.success) {
				this.casesTotalCount = searchResult.data.totalCount;
				if (this.tablePage === 0) {
					this.filteredCases = searchResult.data.items;
				} else {
					this.filteredCases = [...this.filteredCases, ...searchResult.data.items];
				}
			} else {
				this.filteredCases = [];
				this.casesTotalCount = 0;
			}
			this.casesTableLoading = false;
		},
		loadNextPage() {
			this.tablePage++;
			this.doSearch();
		},
	},
	mounted() {
		if (this.$route.query.searchTerm) {
			this.request.searchTerm = String(this.$route.query.searchTerm);
		}
		this.doSearch();
	},
});
</script>
